import React, { useState } from 'react';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { cn } from '@/utils';

export interface PasswordInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  error?: string;
  helperText?: string;
  showStrength?: boolean;
  strength?: 'weak' | 'medium' | 'strong';
}

const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      label = '密码',
      error,
      helperText,
      showStrength = false,
      strength,
      className,
      id,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const inputId = id || 'password-input';

    const strengthColors = {
      weak: 'bg-error-500',
      medium: 'bg-warning-500',
      strong: 'bg-success-500',
    };

    const strengthTexts = {
      weak: '弱',
      medium: '中',
      strong: '强',
    };

    return (
      <div className="flex flex-col gap-1 w-full">
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-gray-700">
            {label}
          </label>
        )}

        <div className="relative">
          {/* 左侧锁图标 */}
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <Lock className="w-5 h-5" />
          </div>

          {/* 密码输入框 */}
          <input
            ref={ref}
            id={inputId}
            type={showPassword ? 'text' : 'password'}
            className={cn(
              'w-full rounded-lg border border-gray-300 pl-10 pr-12 py-2 text-gray-900 placeholder-gray-400',
              'focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20',
              'disabled:bg-gray-50 disabled:cursor-not-allowed',
              error && 'border-error-500 focus:border-error-500 focus:ring-error-500/20',
              className
            )}
            {...props}
          />

          {/* 右侧显示/隐藏按钮 */}
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            tabIndex={-1}
          >
            {showPassword ? (
              <EyeOff className="w-5 h-5" />
            ) : (
              <Eye className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* 密码强度指示器 */}
        {showStrength && strength && (
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full transition-all duration-300',
                  strengthColors[strength]
                )}
                style={{
                  width:
                    strength === 'weak'
                      ? '33%'
                      : strength === 'medium'
                      ? '66%'
                      : '100%',
                }}
              />
            </div>
            <span className="text-xs text-gray-600">
              强度: {strengthTexts[strength]}
            </span>
          </div>
        )}

        {error && <p className="text-sm text-error-600">{error}</p>}
        {helperText && !error && <p className="text-sm text-gray-500">{helperText}</p>}
      </div>
    );
  }
);

PasswordInput.displayName = 'PasswordInput';

export default PasswordInput;
